import { MenuCloud } from './MenuCloud';
import { buildSettingsRows, isMuted } from './SettingsRows';
import { playUiClick } from './UiSound';

export interface WorldEntry {
  file: string;
  name: string;
  author: string;
  description?: string;
  size?: string;
  difficulty?: string;
  icon?: string;
}

interface MenuCallbacks {
  onSelectBundled: (entry: WorldEntry) => void;
  onUpload: (file: File) => void;
  onTutorial: () => void;
  hasContinue: () => boolean;
}

const DATA_BASE = `${import.meta.env.BASE_URL}data/`;

type View = 'main' | 'levels' | 'credits' | 'settings';

/**
 * Recreates yknytt's main menu: a black screen with drifting cloud particles
 * and a row of buttons across the bottom, plus the world-selection list.
 */
export class Menu {
  private el: HTMLDivElement;
  private cloud: MenuCloud;
  private loadingEl: HTMLDivElement | null = null;
  private music: HTMLAudioElement;
  private musicStarted = false;
  private view: View = 'main';

  constructor(
    private parent: HTMLElement,
    private worlds: WorldEntry[],
    private cb: MenuCallbacks,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'knytt-menu';
    this.cloud = new MenuCloud(this.el);
    parent.appendChild(this.el);

    this.music = new Audio();
    // Deferred until startMusic()'s play() call — a bare `new Audio(url)` starts
    // fetching the (1.28MB) track immediately, competing with the JS bundle on load.
    this.music.preload = 'none';
    this.music.loop = true;
    this.music.volume = 0.55;
    this.music.muted = isMuted();
    this.music.src = `${DATA_BASE}Music/Song20.ogg`;

    this.renderMain();
  }

  private startMusic(): void {
    if (this.musicStarted) return;
    this.musicStarted = true;
    this.music.play().catch(() => {
      /* will retry on next interaction */
      this.musicStarted = false;
    });
  }

  /** The dot cloud only appears on the main screen (level select etc. are plain white). */
  private setCloudVisible(visible: boolean): void {
    this.cloud.canvas.style.display = visible ? '' : 'none';
    if (visible) this.cloud.start();
    else this.cloud.stop();
  }

  // ---- Main menu (button row at the bottom) ----
  private renderMain(): void {
    this.view = 'main';
    this.setCloudVisible(true);
    this.clearContent();
    const content = document.createElement('div');
    content.className = 'menu-content';

    const row = document.createElement('div');
    row.className = 'menu-buttonrow';
    // yknytt's exact main-menu row (MainMenu.tscn): Tutorial, Play, Download Levels,
    // Credits, Settings. (Web build has no Quit.) The first button reads
    // "Continue" once a last-played world exists (MainMenu.cs _Ready).
    const buttons: [string, () => void][] = [
      [this.cb.hasContinue() ? 'Continue' : 'Tutorial', () => this.act(() => this.cb.onTutorial())],
      ['Play', () => this.act(() => this.renderLevels())],
      ['Download Levels', () => this.act(() => this.openFilePicker())],
      ['Credits', () => this.act(() => this.renderCredits())],
      ['Settings', () => this.act(() => this.renderSettings())],
    ];
    for (const [label, fn] of buttons) {
      const b = document.createElement('button');
      b.className = 'knytt-btn';
      b.textContent = label;
      b.addEventListener('click', fn);
      row.appendChild(b);
    }
    content.appendChild(row);
    this.el.appendChild(content);
  }

  /** Open a .knytt.bin file picker (the web equivalent of fetching extra levels). */
  private openFilePicker(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.bin,.knytt.bin';
    input.addEventListener('change', () => {
      const f = input.files?.[0];
      if (f) this.act(() => this.cb.onUpload(f));
    });
    input.click();
  }

  // ---- Level selection: a 2-column grid of worlds + filter row (yknytt LevelSelection) ----
  private levelLimit = 6;
  private filters = { difficulty: '[All]', size: '[All]', sort: '[Default]', search: '' };

  private renderLevels(): void {
    this.view = 'levels';
    this.setCloudVisible(false);
    this.levelLimit = 6;
    this.filters = { difficulty: '[All]', size: '[All]', sort: '[Default]', search: '' };
    this.clearContent();
    const content = document.createElement('div');
    content.className = 'menu-content levels';

    const grid = document.createElement('div');
    grid.className = 'levels-grid';
    const more = document.createElement('a');
    more.className = 'levels-more';
    more.href = '#';
    more.textContent = 'Show more levels';
    more.addEventListener('click', (e) => {
      e.preventDefault();
      playUiClick();
      this.levelLimit += 6;
      this.renderGrid(grid, more);
    });

    const opts = (vals: string[]): string => vals.map((v) => `<option>${v}</option>`).join('');
    const filterRow = document.createElement('div');
    filterRow.className = 'levels-filters';
    filterRow.innerHTML = `
      <label>Category:<select disabled>${opts(['[All]'])}</select></label>
      <label>Difficulty:<select data-f="difficulty">${opts(this.uniq('difficulty'))}</select></label>
      <label>Size:<select data-f="size">${opts(this.uniq('size'))}</select></label>
      <label>Sort by:<select data-f="sort">${opts(['[Default]', 'Name', 'Author'])}</select></label>
      <label>Search:<input data-f="search" type="text" /></label>
      <button class="knytt-btn levels-back">Back</button>`;
    filterRow.querySelectorAll<HTMLSelectElement | HTMLInputElement>('[data-f]').forEach((ctrl) => {
      const key = ctrl.getAttribute('data-f') as keyof typeof this.filters;
      const evt = ctrl.tagName === 'INPUT' ? 'input' : 'change';
      ctrl.addEventListener(evt, () => {
        this.filters[key] = ctrl.value;
        this.levelLimit = 6;
        this.renderGrid(grid, more);
      });
    });
    (filterRow.querySelector('.levels-back') as HTMLButtonElement).addEventListener('click', () =>
      this.act(() => this.renderMain()),
    );

    content.appendChild(grid);
    content.appendChild(more);
    content.appendChild(filterRow);
    this.el.appendChild(content);
    this.renderGrid(grid, more);
  }

  private uniq(key: 'difficulty' | 'size'): string[] {
    const vals = new Set<string>();
    for (const w of this.worlds) if (w[key]) vals.add(w[key] as string);
    return ['[All]', ...[...vals].sort()];
  }

  private filteredWorlds(): WorldEntry[] {
    let list = this.worlds.filter((w) => {
      if (this.filters.difficulty !== '[All]' && w.difficulty !== this.filters.difficulty) return false;
      if (this.filters.size !== '[All]' && w.size !== this.filters.size) return false;
      const q = this.filters.search.trim().toLowerCase();
      if (q && !(`${w.name} ${w.author}`.toLowerCase().includes(q))) return false;
      return true;
    });
    if (this.filters.sort === 'Name') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else if (this.filters.sort === 'Author') list = [...list].sort((a, b) => a.author.localeCompare(b.author));
    return list;
  }

  private renderGrid(grid: HTMLDivElement, more: HTMLAnchorElement): void {
    grid.innerHTML = '';
    const all = this.filteredWorlds();
    const shown = all.slice(0, this.levelLimit);
    for (const w of shown) {
      const item = document.createElement('button');
      item.className = 'level-cell';
      // WorldEntry.cs CompletionColor: completed (any ending recorded in the save,
      // Win.checkLevelCompleted) tints the cell grey (0.75); an existing save
      // without an ending tints it pale green (0.85, 1, 0.85).
      const saveState = worldSaveState(w.file);
      if (saveState === 'completed') item.style.backgroundColor = '#bfbfbf';
      else if (saveState === 'saved') item.style.backgroundColor = '#d9ffd9';
      const iconHtml = w.icon
        ? `<img class="lv-icon" src="${DATA_BASE}${w.icon}" alt="" />`
        : `<span class="lv-icon"></span>`;
      item.innerHTML = `${iconHtml}<span class="lv-text"><span class="lv-name"></span><span class="lv-desc"></span></span>`;
      (item.querySelector('.lv-name') as HTMLElement).textContent = `${w.name} (${w.author})`;
      (item.querySelector('.lv-desc') as HTMLElement).textContent = w.description || '';
      item.addEventListener('click', () => this.act(() => this.cb.onSelectBundled(w)));
      grid.appendChild(item);
    }
    more.style.display = all.length > this.levelLimit ? '' : 'none';
  }

  private renderSettings(): void {
    this.view = 'settings';
    this.setCloudVisible(false);
    this.clearContent();
    const content = document.createElement('div');
    content.className = 'menu-content credits';
    content.innerHTML = `
      <div class="levels-title">Settings</div>
      <div class="credits-body"></div>
      <div class="levels-actions"><button class="knytt-btn" id="back">Back</button></div>`;
    (content.querySelector('.credits-body') as HTMLElement).appendChild(
      buildSettingsRows((muted) => (this.music.muted = muted)),
    );
    (content.querySelector('#back') as HTMLButtonElement).addEventListener('click', () =>
      this.act(() => this.renderMain()),
    );
    this.el.appendChild(content);
  }

  private renderCredits(): void {
    this.view = 'credits';
    this.setCloudVisible(false);
    this.clearContent();
    const content = document.createElement('div');
    content.className = 'menu-content credits';
    content.innerHTML = `
      <div class="levels-title">Credits</div>
      <div class="credits-body">
        <p><b>Knytt Stories</b> — design, art &amp; music by <b>Nifflas</b>.</p>
        <p>Engine reference &amp; default content: the <b>yknytt</b> project.</p>
        <p>This is a TypeScript / web reimplementation of the yknytt engine.</p>
      </div>
      <div class="levels-actions">
        <button class="knytt-btn" id="back">Back</button>
      </div>`;
    (content.querySelector('#back') as HTMLButtonElement).addEventListener('click', () =>
      this.act(() => this.renderMain()),
    );
    this.el.appendChild(content);
  }

  /** Run an action, ensuring menu music has been kicked off by the gesture. */
  private act(fn: () => void): void {
    playUiClick(); // yknytt ClickPlayer on every menu button/entry
    this.startMusic();
    fn();
  }

  private clearContent(): void {
    for (const c of Array.from(this.el.querySelectorAll('.menu-content'))) c.remove();
  }

  // ---- External API used by the app ----
  show(): void {
    this.clearLoading();
    this.el.classList.remove('hidden');
    this.cloud.start();
    this.renderMain();
  }

  hide(): void {
    this.clearLoading();
    this.el.classList.add('hidden');
    this.cloud.stop();
    this.music.pause();
  }

  showLoading(message: string): void {
    this.clearLoading();
    this.loadingEl = document.createElement('div');
    this.loadingEl.className = 'loading';
    this.loadingEl.innerHTML = `<div class="spinner"></div><div class="loading-msg"></div>`;
    (this.loadingEl.querySelector('.loading-msg') as HTMLElement).textContent = message;
    this.parent.appendChild(this.loadingEl);
  }

  showError(message: string): void {
    this.clearLoading();
    this.show();
    this.banner(message, 'error', 5000);
  }

  private banner(text: string, kind: string, ms: number): void {
    const b = document.createElement('div');
    b.className = `menu-banner ${kind}`;
    b.textContent = text;
    this.parent.appendChild(b);
    setTimeout(() => b.remove(), ms);
  }

  private clearLoading(): void {
    this.loadingEl?.remove();
    this.loadingEl = null;
  }
}

/**
 * Read a bundled world's save state from localStorage (`knytt-save:<worldDir>`;
 * the world directory is the bin filename minus `.knytt.bin`): 'completed' if
 * any ending is recorded, 'saved' if a save exists, else null.
 */
function worldSaveState(file: string): 'completed' | 'saved' | null {
  try {
    const dir =
      globalThis.localStorage?.getItem(`knytt-dir:${file}`) ?? file.replace(/\.knytt\.bin$/i, '');
    const key = `knytt-save:${dir}`;
    const save = globalThis.localStorage?.getItem(key);
    if (!save) return null;
    const m = /^\s*Endings\s*=\s*(.+)\s*$/im.exec(save);
    return m && m[1].trim() ? 'completed' : 'saved';
  } catch {
    return null;
  }
}
